<template>
  <!--content-->
  <section v-if="buyFlowStore.pageState === pageStates.reviewOrder">
    <DisplayShippingInfo v-if="FLAGS.ENABLE_BUYFLOW_WEBROOT && buyFlowStore.hasShippableItem" class="mb" />
    <!--Permission label and expiration-->
    <ul class="form">
      <li v-if="!isUSCreditCard" class="checkbox">
        <input id="OptInGDPR" v-model="emailOptIn" class="no-shrink" type="checkbox" />
        <label for="OptInGDPR">{{ tn("GDPREmailOptIn") }}</label>
      </li>
      <li class="checkbox">
        <input id="acceptTos" v-model="permissionChecked" class="no-shrink" type="checkbox" />
        <label for="acceptTos">{{ tn("permissionLabel") }}</label>
      </li>
    </ul>
    <p v-if="!isManualRenewEnabledByDefault" id="renew-date-text" class="mb-double">
      {{
        tn("thisSubscriptionWillAutomaticallyRenew", {
          date: getRenewDate(),
        })
      }}
    </p>
    <p v-else id="renew-date-text" class="mb-double">
      {{
        tn("thisSubscriptionIsValidUntil", {
          date: getRenewDate(),
        })
      }}
    </p>
    <!--Checkout button-->
    <div class="mb">
      <button
        id="btnCheckOut"
        class="btn-primary mb"
        :disabled="!creditCard || !permissionChecked"
        @click="goToNextAction"
      >
        {{ tn("secureCheckout") }}
      </button>
    </div>
    <div class="mb">
      <svg-icon-component v-if="!buyFlowStore.hasWebrootCartItems()" icon="trust-badge-30" class="no-shrink mr" />
      <svg-icon-component v-if="buyFlowStore.hasWebrootCartItems()" icon="trust-badge-70" class="no-shrink mr" />
      <svg-icon-component icon="trust-badge-support" class="no-shrink mr" />
    </div>
    <BuyDisclaimers :show-auto-renew-links="!isManualRenewEnabledByDefault" />
    <AutomaticRenew
      :show-dialog="displayAutoRenewalDialog"
      @continue-purchase="closeAutoRenewAndContinue"
      @close-modal="toggleAutoRenewDialog"
    />
    <CheckHasRecentPurchase
      :show-dialog="displayCheckHasRecentPurchaseDialog"
      :term-length="termLength"
      :computer-name="computerName"
      @close-modal="continueWithPurchase"
    />
  </section>
</template>

<script setup lang="ts">
//Components
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import BuyDisclaimers from "@/components/Buy/BuyDisclaimers.vue";
import AutomaticRenew from "../Dialogs/AutomaticRenew.vue";
import CheckHasRecentPurchase from "@/components/Buy/Dialogs/CheckHasRecentPurchase.vue";
import DisplayShippingInfo from "@/components/Buy/ReviewOrder/DisplayShippingInfo.vue";
//Other imports
import { getRenewDate, handlePurchaseError, setShippingAddressFromCC } from "@/components/Buy/BuyHelper";
import { logEvent, logException } from "@/common/logger";
import { t } from "@/i18n";
import { computed, ref } from "vue";
import { useNotificationsStore } from "@/stores/notifications";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { unifiedApi } from "@/common";
import { pageStates } from "@/components/Buy/BuyEnums";
import { useRouter } from "vue-router";
import { AxiosError } from "axios";
import { IPurchaseError } from "../Interfaces";
import { autoRenewOptInCountries, autoRenewOptInUSStates } from "../Payment/PaymentConfigs";
import { DEFAULT_TERM_LENGTH_MONTHS, UNITED_STATES, FLAGS } from "@/define";
import { useUserStore } from "@/stores/user";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { cartUpdate, googleTagCartEvents } from "@/common/googleTagEvents";

const permissionChecked = ref<boolean>(false);
const emailOptIn = ref<boolean>(false);
const displayAutoRenewalDialog = ref<boolean>(false);
const displayCheckHasRecentPurchaseDialog = ref<boolean>(false);
const notificationsStore = useNotificationsStore();
const buyFlowStore = useBuyFlowStore();
const router = useRouter();
const userStore = useUserStore();
const subscriptionStore = useSubscriptionsStore();
const fiveMinutesInMilliseconds = 300000;

const creditCard = computed(() => {
  return buyFlowStore.creditCards.find(c => c.id === buyFlowStore.cart?.paymentMethodId);
});

const isUSCreditCard = computed(() => creditCard.value?.address?.country === UNITED_STATES);

const componentName = "ReviewOrderComponent";

logEvent("created", componentName);

const isManualRenewEnabledByDefault = computed(() => {
  const cc = buyFlowStore.creditCards.find(l => l.id === buyFlowStore.cart?.paymentMethodId);

  return autoRenewOptInUSStates.includes(cc?.address?.stateOrProvince || "");
});

const computerName = computed(() => {
  if (subscriptionStore.subscriptions.length > 0) {
    const itemInCart = buyFlowStore.cart?.items.find(l => l.computerId > 0);
    if (itemInCart) {
      const sub = subscriptionStore.subscriptions.find(l => l.computer?.id === itemInCart.computerId);

      if (sub) {
        return sub.computer?.description || sub.computer?.name || "";
      }
    }
  }

  return buyFlowStore.computerName || "";
});

const termLength = computed(() => {
  const itemInCart = buyFlowStore.cart?.items.find(l => l.computerId > 0);

  if (itemInCart) {
    const plan = buyFlowStore.plans.find(l => l.sku === itemInCart.sku);
    if (plan) {
      const ratePlan = plan.ratePlans.find(l => l.ratePlanId === itemInCart.ratePlanId);

      if (ratePlan?.months) {
        return ratePlan.months / 12;
      }
    }
  }

  return DEFAULT_TERM_LENGTH_MONTHS / 12;
});

function toggleAutoRenewDialog() {
  displayAutoRenewalDialog.value = !displayAutoRenewalDialog.value;
}

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

async function goToNextAction() {
  //Decide if auto-renewal dialog should be displayed
  //auto-renewal dialog handles checkout on its own
  if (
    creditCard.value &&
    creditCard.value.address.country === autoRenewOptInCountries.us.toString() &&
    autoRenewOptInUSStates.includes(creditCard.value.address.stateOrProvince) &&
    !buyFlowStore.checkSalesTax
  ) {
    displayAutoRenewalDialog.value = true;
    return;
  }

  //default path
  await CheckHasRecentPurchaseForComputer();
}

async function closeAutoRenewAndContinue() {
  toggleAutoRenewDialog();
  await CheckHasRecentPurchaseForComputer();
}

async function CheckHasRecentPurchaseForComputer() {
  const itemWithId = buyFlowStore.cart?.items.find(l => l.computerId > 0);
  if (itemWithId && userStore.currentUser) {
    try {
      if (buyFlowStore.recentPurchase && buyFlowStore.recentPurchase.computerId === itemWithId.computerId) {
        const now = Date.now();
        const difference = now - buyFlowStore.recentPurchase.purchasedOnMilliseconds;
        if (difference < fiveMinutesInMilliseconds) {
          //Show dialog
          displayCheckHasRecentPurchaseDialog.value = true;
        } else {
          checkOut();
        }
      } else {
        const response = (await unifiedApi.checkHasRecentPurchase(itemWithId.computerId)).data;

        if (response && response.success) {
          if (response.hasRecentPurchase) {
            //Show dialog
            displayCheckHasRecentPurchaseDialog.value = true;
          } else {
            checkOut();
          }
        } else {
          notificationsStore.addNotification({ type: "ServerErrors", dismissible: false });
          logEvent(`CheckHasRecentPurchase API call was not successful, ${response.errorMessage}`, componentName);
        }
      }
    } catch (err) {
      logException(err as AxiosError, false);
      notificationsStore.addNotification({ type: "ServerErrors", dismissible: false });
    }
  } else {
    await checkOut();
  }
}

function continueWithPurchase(continuePurchase: boolean) {
  if (continuePurchase) {
    checkOut();
  }

  displayCheckHasRecentPurchaseDialog.value = false;
}

async function checkOut() {
  try {
    notificationsStore.clearNotifications();
    if (buyFlowStore.cart) {
      buyFlowStore.showProcessingOrderMessage = true;
      buyFlowStore.cart.userGuid = buyFlowStore.userGuid;
      buyFlowStore.cart.emailOptIn = emailOptIn.value;
      if (buyFlowStore.hasShippableItem && buyFlowStore.shippingMatchesBilling === true && creditCard.value?.address) {
        setShippingAddressFromCC(creditCard.value);
      }
      buyFlowStore.cart = (await unifiedApi.purchaseShoppingCart(buyFlowStore.cart)).data;
      cartUpdate(buyFlowStore.cart, googleTagCartEvents.purchase);
      //await awaitAsyncPurchaseProcess();
      buyFlowStore.showProcessingOrderMessage = false;
      displayAutoRenewalDialog.value = false;
      const itemWithId = buyFlowStore.cart?.items.find(l => l.computerId > 0);
      if (itemWithId) {
        buyFlowStore.recentPurchase = { computerId: itemWithId.computerId, purchasedOnMilliseconds: Date.now() };
      }
      setTimeout(() => router.push({ name: "orderConfirmation" }), 500);
    } else {
      //Throw an error. This should never happen
      //buyFlowStore.showProcessingOrderMessage = false;
      throw new Error("cart is undefined");
    }
  } catch (error) {
    buyFlowStore.showProcessingOrderMessage = false;
    const err = error as AxiosError;
    const response = err?.response?.data as IPurchaseError;

    if (response) {
      handlePurchaseError(response.message, err?.response?.status);
    } else {
      notificationsStore.addNotification({ type: "PurchaseFailureError", dismissible: false });
    }

    displayAutoRenewalDialog.value = false;
    logException(error as Error, `${componentName}: processPurchase`);
  }
}
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
